import { TrustedDeviceSecret } from "@/models";
import { NativeAppConfig } from "@/utils/env";

export enum NativeAppType {
  Web = 0,
  Android = 1,
  iOS = 2
}

export enum NativeAppPage {
  BiometricSettings = "BIOMETRIC_SETTINGS"
}

export interface NativeAppInfo {
  device: {
    make: string;
    model: string;
    pushToken?: string;
  };
  build: {
    number: string;
    date: string;
  };
  /** Indicates which pages the device supports navigating to */
  nativePages: {
    [page in NativeAppPage]?: boolean;
  };
  hasBiometricsHardware: boolean;
}

export interface VueAppInitialized {
  jwt?: string;
  config?: NativeAppConfig;
}

export interface VueAppLoggedIn {
  jwt?: string;
  username?: string;
}

export interface GetTrustedDeviceSecretResponse {
  secret?: TrustedDeviceSecret;
}

export interface VueAppNavigatedToPatient {
  patientId: number;
  tab?: PatientTab;
}

export enum CheckBiometricsStatus {
  UNKNOWN_ERROR = 1,
  NO_HARDWARE = 2,
  NOT_CONFIGURED = 3,
  NO_PERMISSION = 4,

  CONFIGURED = 100
}

export interface CheckBiometricsResponse {
  status: CheckBiometricsStatus;
}

export enum SetupBiometricsStatus {
  UNKNOWN_ERROR = 1,
  CANCELED = 2,
  NOT_CONFIGURED = 3,
  NO_PERMISSION = 4,
  FAILED = 5,
  LOCKED_OUT = 6,

  SUCCESS = 100
}

export interface SetupBiometricsResponse {
  status: SetupBiometricsStatus;
}

export enum DecryptBiometricSecretStatus {
  UNKNOWN_ERROR = 1,
  CANCELED = 2,
  NOT_CONFIGURED = 3,
  NO_PERMISSION = 4,
  FAILED = 5,
  LOCKED_OUT = 6,

  BIOMETRICS_CHANGED = 50,

  SUCCESS = 100
}

export type DecryptBiometricSecretResponse =
  | DecryptBiometricSecretResponseSuccess
  | DecryptBiometricSecretResponseFailed;

export interface DecryptBiometricSecretResponseSuccess {
  status: DecryptBiometricSecretStatus.SUCCESS;
  email: string;
  secret: string;
}

export interface DecryptBiometricSecretResponseFailed {
  status: Exclude<DecryptBiometricSecretStatus, DecryptBiometricSecretStatus.SUCCESS>;
}

export enum PatientTab {
  Info = "INFO",
  Infusion = "INFUSION",
  Messages = "MESSAGES"
}

export enum DeviceType {
  Mobile = "Mobile",
  Web = "Web"
}

export interface BuildInfo {
  full: string;
  version: string;
  revision: string;
  date: string;
}

export interface NativeApp {
  readonly type: NativeAppType;
  readonly isMobile: boolean;
  readonly buildInfo: BuildInfo;

  readonly deviceMake?: string;
  readonly deviceModel?: string;
  readonly deviceOS?: string;
  readonly deviceType?: DeviceType;

  readonly hasBiometricsHardware: boolean;
  readonly isUsingBiometrics: boolean;

  /**
   * Returns true if the native app supports navigating to the specified page, otherwise returns false.
   */
  canNavigateTo(page: NativeAppPage): boolean;

  /**
   * Navigates to the specified page on the native app
   */
  navigateTo(page: NativeAppPage): void;

  /**
   * Should be fired when Vue.js navigates to a patient page so that the native app can remove any notifications for
   * the patient
   * @param patientId
   */
  navigatedToPatient(patientId: number, tab?: PatientTab): void;

  /**
   * Saves the trusted device secret to the native app so that it can be stored securely since it is used to bypass 2FA
   */
  saveTrustedDeviceSecret(secret: TrustedDeviceSecret): void;

  /**
   * Gets the trusted device secret, which is used during login to attempt to bypass 2FA. Since multiple users can use
   * a single device, specify the user's email of the device secret that should be retrieved. The email needs to be
   * provided (vs. using auth.user.email) because getting the trusted device secret will happen before logging in.
   */
  getTrustedDeviceSecret(email: string): Promise<TrustedDeviceSecret | undefined>;

  /**
   * Called when wanting to enable biometrics. If permissions are required and not yet granted, the native app will
   * prompt the user for permissions (applicable to iOS for Face ID). If the user has not configured their biometrics
   * the user will be prompted explaining that they need to configure their biometrics.
   *
   * @returns a promise indciating success if the user granted permission AND biometrics are configured.
   */
  checkBiometrics(): Promise<CheckBiometricsResponse>;

  /**
   * Called to save the biometric secret to the device using the users biometrics.
   *
   * @returns a promise indicating success if the user was succesfully able to complete the biometric prompt. If the
   * user canceled, did not grant permission, or did not configure biometrics the return value is unsuccessful.
   */
  setupBiometrics(secret: string): Promise<SetupBiometricsResponse>;

  /**
   * Tells the native app to prompt the user to perform biometric validation in order to decrypt the user's biometric
   * secret (for performing a biometric login).
   *
   * @returns a promise which, if successful, has the user's email and biometric secret. The return value also
   * indicates if the user canceled the biometric prompt or if the users biometrics have changed since the secret was
   * encrypted (which invalidates the secret)
   */
  decryptBiometricSecret(): Promise<DecryptBiometricSecretResponse>;

  /**
   * Tells the native app that biometrics are no longer being used and to remove the biometric secret.
   */
  removeBiometrics(): void;
}

// Extend the Vue interface to have the $nativeApp property
declare module "vue/types/vue" {
  interface Vue {
    $nativeApp: NativeApp;
  }
}
